// Строгий decoder компактного офлайн-QR УРВ (транспорт WQ1, RD-177/RD-178).
// Формат: "WQ1:" + Base64URL без padding от ровно 85 байт = 118 ASCII-символов.
// WQ1 — версия транспорта; version внутри данных — прежняя версия подписи (только 1).
// Подпись здесь не проверяется: её владелец — backend.

export interface UrvOfflineCheckinPayload {
    version: number
    user_id: string
    device_id: string
    shift_id: string
    issued_at: number
    signature: string
}

const wq1Prefix = 'WQ1:'
const wq1PayloadByteLength = 85
const wq1BodyLength = 114
const wq1TotalLength = wq1Prefix.length + wq1BodyLength
const supportedSignatureVersion = 1
const base64UrlAlphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_'

const uuidOffsets = {
    user_id: 1,
    device_id: 17,
    shift_id: 33,
} as const
const uuidByteLength = 16
const issuedAtOffset = 49
const signatureOffset = 53
const signatureByteLength = 32

function decodeBase64UrlBody(body: string): Uint8Array | null {
    const bytes = new Uint8Array(wq1PayloadByteLength)
    let buffer = 0
    let bufferedBits = 0
    let byteIndex = 0

    for (const char of body) {
        const value = base64UrlAlphabet.indexOf(char)
        if (value < 0) return null

        buffer = (buffer << 6) | value
        bufferedBits += 6

        if (bufferedBits >= 8) {
            bufferedBits -= 8
            if (byteIndex >= wq1PayloadByteLength) return null
            bytes[byteIndex] = (buffer >> bufferedBits) & 0xff
            byteIndex += 1
        }

        buffer &= (1 << bufferedBits) - 1
    }

    // Каноничность: все байты на месте, неиспользуемые биты последнего символа нулевые.
    if (byteIndex !== wq1PayloadByteLength || buffer !== 0) return null

    return bytes
}

function bytesToHex(bytes: Uint8Array, offset: number, length: number): string {
    let hex = ''

    for (let index = offset; index < offset + length; index += 1) {
        hex += bytes[index].toString(16).padStart(2, '0')
    }

    return hex
}

function bytesToUuid(bytes: Uint8Array, offset: number): string {
    const hex = bytesToHex(bytes, offset, uuidByteLength)

    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

export function decodeUrvOfflineCheckinQr(qrText: string): UrvOfflineCheckinPayload | null {
    if (qrText.length !== wq1TotalLength || !qrText.startsWith(wq1Prefix)) return null

    const bytes = decodeBase64UrlBody(qrText.slice(wq1Prefix.length))
    if (!bytes) return null

    const version = bytes[0]
    if (version !== supportedSignatureVersion) return null

    const issuedAt = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint32(issuedAtOffset, false)
    if (issuedAt < 1) return null

    return {
        version,
        user_id: bytesToUuid(bytes, uuidOffsets.user_id),
        device_id: bytesToUuid(bytes, uuidOffsets.device_id),
        shift_id: bytesToUuid(bytes, uuidOffsets.shift_id),
        issued_at: issuedAt,
        signature: bytesToHex(bytes, signatureOffset, signatureByteLength),
    }
}
